/**
 * ================================================================
 * CUSTOM HOOK: useNotificationSocket
 * ================================================================
 *
 * @hook             useNotificationSocket
 * @description      Hook for managing real-time notifications via Socket.io
 * @category         Business Logic Hook
 *
 * @parameters
 * - token: string - Authentication token for socket connection
 * - uid: string - User identifier for notification targeting
 *
 * @returns
 * - isConnected: boolean - Socket connection status
 * - notifications: Notification[] - Real-time notifications array
 * - unreadCount: number - Count of unread notifications
 * - showNotificationPopup: Function - Show notification popup
 * - clearNotifications: Function - Clear notifications array
 *
 * @sideEffects
 * - Socket connection: Establishes WebSocket connection to server
 * - Real-time updates: Listens for new notifications
 * - Popup display: Shows toast notifications for new messages
 *
 * @dependencies
 * - socket.io-client: Real-time communication
 * - RTK Query: Cache invalidation for notifications
 * - Toast notifications: User feedback system
 *
 * @usage
 * ```tsx
 * const {
 *   isConnected,
 *   notifications,
 *   unreadCount,
 *   showNotificationPopup
 * } = useNotificationSocket(token, uid);
 * ```
 * ================================================================
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

import { useToast } from '@/components/ui/toast';
import { notificationsApi } from '@/services/notificationsService';
import { useAppDispatch } from '@/store/hooks';
import type { Notification, NotificationItemStatus } from '@/types/notifications';

interface UseNotificationSocketOptions {
  /** Authentication token for socket connection */
  token: string;
  /** User identifier for notification targeting */
  uid: string;
  /** Enable/disable socket connection */
  enabled?: boolean;
  /** Socket server URL */
  serverUrl?: string;
}

interface NotificationPopupData {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  timestamp: string;
}

export function useNotificationSocket({
  token,
  uid,
  enabled = true,
  serverUrl = process.env.VITE_SOCKET_URL,
}: UseNotificationSocketOptions) {
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const socketRef = useRef<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // ================================================================
  // NOTIFICATION POPUP HANDLER
  // ================================================================
  //
  // Purpose: Display toast notification for new messages
  // UX: Non-intrusive popup with action buttons
  // Accessibility: Screen reader compatible notifications
  // ================================================================

  const showNotificationPopup = useCallback(
    (data: NotificationPopupData) => {
      const { title, message, type } = data;

      // Show toast notification with appropriate styling
      toast({
        title: title,
        description: message,
        variant: type === 'error' ? 'destructive' : 'default',
        duration: 5000,
      });

      // Add notification sound (optional)
      try {
        const audio = new Audio('/notification-sound.mp3');
        audio.volume = 0.3;
        audio.play().catch(() => {
          // Ignore audio play errors (user interaction required)
        });
      } catch (error) {
        console.warn('Notification sound failed:', error);
      }
    },
    [toast],
  );

  // ================================================================
  // SOCKET CONNECTION MANAGEMENT
  // ================================================================
  //
  // Purpose: Establish and manage WebSocket connection
  // Authentication: Token-based authentication
  // Reconnection: Automatic reconnection on disconnect
  // ================================================================

  useEffect(() => {
    if (!enabled || !token || !uid) {
      return;
    }

    // Initialize socket connection
    const socket = io(serverUrl, {
      auth: {
        token: token,
        uid: uid,
      },
      withCredentials: true,
      transports: ['websocket', 'polling'],
      timeout: 5000,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });

    socketRef.current = socket;

    // ================================================================
    // CONNECTION EVENT HANDLERS
    // ================================================================

    socket.on('connect', () => {
      console.log('🔌 Socket connected:', socket.id);
      setIsConnected(true);
    });

    socket.on('disconnect', (reason) => {
      console.log('🔌 Socket disconnected:', reason);
      setIsConnected(false);
    });

    socket.on('connect_error', (error) => {
      console.error('🔌 Socket connection error:', error);
      setIsConnected(false);
    });

    // ================================================================
    // AUTHENTICATION EVENT HANDLERS
    // ================================================================

    socket.on('authenticated', (data) => {
      console.log('✅ User authenticated:', data);
      setIsConnected(true);
    });

    socket.on('authentication_error', (error) => {
      console.error('❌ Authentication failed:', error);
      setIsConnected(false);

      // Show error toast
      toast({
        title: 'Authentication Failed',
        description: 'Unable to connect to notification service. Please refresh the page.',
        variant: 'destructive',
      });
    });

    // ================================================================
    // NOTIFICATION EVENT HANDLERS
    // ================================================================

    socket.on('newNotification', (data: any) => {
      console.log('✅ New notification received:', data);

      try {
        // Parse notification data
        const notification: Notification = {
          id: data.id || Date.now().toString(),
          notification_id: data.notification_id || data.id || Date.now().toString(),
          title: data.title || 'New Notification',
          description: data.message || data.description || '',
          status: 'UNREAD' as NotificationItemStatus,
          created_at: data.timestamp || new Date().toISOString(),
          read_at: null,
          delivery_status: 'SENT',
          app: data.app || 'backoffice',
          feature: data.feature || 'general',
        };

        // Add to local notifications array
        setNotifications((prev) => [notification, ...prev]);

        // Update unread count
        setUnreadCount((prev) => prev + 1);

        // Show popup notification
        showNotificationPopup({
          id: notification.id,
          title: notification.title,
          message: notification.description,
          type: data.type || 'info',
          timestamp: notification.created_at,
        });

        // Invalidate RTK Query cache to refresh notification lists
        dispatch(notificationsApi.util.invalidateTags(['Notifications']));
      } catch (error) {
        console.error('Error processing new notification:', error);
      }
    });

    // 🔹 Event: Single notification read
    socket.on('notificationStatusUpdate', (data) => {
      console.log('📩 Notification status updated:', data);

      // Update state if needed
      setNotifications((prev) =>
        prev.map((n) => (n.id === data.notification_id ? { ...n, status: data.status } : n)),
      );

      // Update unread count if notification was marked as read
      if (data.status === 'READ') {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }

      // Invalidate cache to refresh UI
      dispatch(notificationsApi.util.invalidateTags(['Notifications']));
    });

    // 🔹 Event: All notifications read
    socket.on('allNotificationsRead', (data) => {
      console.log('📢 All notifications marked as read:', data);

      // Update state
      setNotifications((prev) => prev.map((n) => ({ ...n, status: 'READ' })));

      // Reset unread count
      setUnreadCount(0);

      // Invalidate cache to refresh UI
      dispatch(notificationsApi.util.invalidateTags(['Notifications']));
    });

    // 🔹 Event: Unread count update
    socket.on('notificationCountUpdate', (data) => {
      console.log('🔄 Unread count updated:', data.unread_count);
      setUnreadCount(data.unread_count);
    });

    // Handle bulk notification updates
    socket.on('notificationsUpdate', (data: any) => {
      console.log('📧 Notifications bulk update:', data);

      // Invalidate cache to fetch fresh data
      dispatch(notificationsApi.util.invalidateTags(['Notifications']));
    });

    // Handle notification read status updates (legacy)
    socket.on('notificationRead', (data: { id: string; status: string }) => {
      console.log('👁️ Notification marked as read:', data);

      setNotifications((prev) =>
        prev.map((notification) =>
          notification.id === data.id
            ? { ...notification, status: data.status as NotificationItemStatus }
            : notification,
        ),
      );

      // Invalidate cache
      dispatch(notificationsApi.util.invalidateTags(['Notifications']));
    });

    // ================================================================
    // CLEANUP
    // ================================================================

    return () => {
      console.log('🔌 Cleaning up socket connection');
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
      socket.off('authenticated');
      socket.off('authentication_error');
      socket.off('newNotification');
      socket.off('notificationStatusUpdate');
      socket.off('allNotificationsRead');
      socket.off('notificationCountUpdate');
      socket.off('notificationsUpdate');
      socket.off('notificationRead');
      socket.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    };
  }, [enabled, token, uid, serverUrl, dispatch, showNotificationPopup]);

  // ================================================================
  // UTILITY FUNCTIONS
  // ================================================================

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const emitNotificationRead = useCallback(
    (notificationId: string) => {
      if (socketRef.current && isConnected) {
        socketRef.current.emit('markNotificationAsRead', {
          id: notificationId,
          uid: uid,
        });
      }
    },
    [isConnected, uid],
  );

  const emitMarkAllAsRead = useCallback(() => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('markAllNotificationsAsRead', {
        uid: uid,
      });
    }
  }, [isConnected, uid]);

  return {
    isConnected,
    notifications,
    unreadCount,
    showNotificationPopup,
    clearNotifications,
    emitNotificationRead,
    emitMarkAllAsRead,
    socket: socketRef.current,
  };
}

/**
 * ================================================================
 * END OF HOOK: useNotificationSocket
 * ================================================================
 *
 * @exports          useNotificationSocket - Real-time notification management
 * @dependencies     socket.io-client, RTK Query, toast notifications
 * @usedBy           Header component, Notification components
 *
 * @performance
 * - Connection pooling: Single socket connection per user
 * - Event cleanup: Proper listener removal on unmount
 * - Memory management: Efficient notification array updates
 *
 * @security
 * - Token authentication: Secure socket authentication
 * - Input validation: Sanitized notification data
 * - Error handling: Graceful failure recovery
 *
 * ================================================================
 */
